import {
  Box,
  Button,
  LinearProgress,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { MdPendingActions } from 'react-icons/md';
import isColorDark from '../../../../../../utils/isColorDark';

export const RootStyled = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.global.white,
  padding: 19,
  minWidth: '780px',
}));

export const HeaderBoxStyled = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const NameStyled = styled(Typography)({
  padding: '0.5rem 0 1rem',
  fontSize: '24px',
});

export const BtnStyled = styled(Button)(({ theme }) => ({
  width: 200,
  height: 42,
  margin: '1rem 0',
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.global.white,
  fontWeight: 'bold',
  textTransform: 'none',
  fontSize: '14px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    filter: 'brightness(92%)',
  },
  '&.Mui-disabled': {
    backgroundColor: '#8E8E8E',
    color: '#FFFFFF',
  },
}));

export const InputBoxStyled = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
  margin: '2rem 0',
  gap: '0.5rem',
});

export const FrequencyInputStyled = styled(TextField)({
  width: 194,
  height: 48,
  fontSize: '14px',
});

export const PeriodsInputStyled = styled(TextField)({
  width: 154,
  height: 48,
  fontSize: '14px',
});

export const DateInputStyled = styled(TextField)({
  width: 250,
  height: 48,
  fontSize: '14px',
});

export const LoadingProgressStyled = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '90vw',
  height: '30vh',
  gap: 16,
});

export const TableContainerStyled = styled(TableContainer)({
  maxHeight: 545,
  position: 'relative',
});

export const TableCellHeaderStickyStyled = styled(TableCell)(({ theme }) => ({
  height: '48px',
  color: isColorDark(theme.palette.primary.main)
    ? theme.palette.global.white
    : theme.palette.global.black,
  fontWeight: 500,
  fontSize: '12px',
  backgroundColor: theme.palette.primary.main,
  textAlign: 'center',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    color: isColorDark(theme.palette.secondary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
  },
  padding: '12px 6px',
  minWidth: '120px',
  left: '0px',
  position: 'sticky',
  zIndex: 99,
  opacity: 1,
}));

export const TableCellHeaderFixedStyled = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  zIndex: 99,
  left: '200px',
  height: '48px',
  color: isColorDark(theme.palette.primary.main)
    ? theme.palette.global.white
    : theme.palette.global.black,
  fontWeight: 500,
  fontSize: '12px',
  backgroundColor: alpha(theme.palette.primary.main, 1),
  textAlign: 'center',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    color: isColorDark(theme.palette.secondary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
  },
  padding: '12px 6px',
  minWidth: '120px',
}));

export const TableCellFooterFixedStyled = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  zIndex: 99,
  left: '0px',
  height: '48px',
  color: isColorDark(theme.palette.primary.main)
    ? theme.palette.global.white
    : theme.palette.global.black,
  fontWeight: 500,
  fontSize: '12px',
  backgroundColor: alpha(theme.palette.primary.main, 1),
  textAlign: 'center',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    color: isColorDark(theme.palette.secondary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
  },
  padding: '12px 6px',
  minWidth: '120px',
}));

export const TableCellHeaderStyled = styled(TableCell)(({ theme }) => ({
  height: '48px',
  color: isColorDark(theme.palette.primary.main)
    ? theme.palette.global.white
    : theme.palette.global.black,
  fontWeight: 500,
  fontSize: '12px',
  backgroundColor: theme.palette.primary.main,
  textAlign: 'center',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
    color: isColorDark(theme.palette.secondary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
  },
  padding: '12px 6px',
  minWidth: '120px',
  left: '0px',
}));

export const TableRowStyled = styled(TableRow)(({ theme }) => ({
  height: '48px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  position: 'relative',
  '&.primary-item': {
    backgroundColor: alpha(theme.palette.primary.main, 0.7),
  },
  '&.secondary-item': {
    backgroundColor: alpha(theme.palette.primary.main, 0.4),
  },
  '&.tertiary-item': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

export const CellStepsStyled = styled(TableCell)(({ theme }) => ({
  fontSize: '12px',
  position: 'sticky',
  left: '0px',
  minWidth: '200px',
  border: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
    color: isColorDark(theme.palette.primary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
  },
  zIndex: 98,
  backgroundColor: theme.palette.primary.main,
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
}));

export const ItemNumStyled = styled('span')({
  fontWeight: 600,
  fontSize: '12px',
  display: 'inline-block',
  width: '40px',
});

export const TableCellFixedStyled = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: '200px',
  minWidth: '64px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: '12px',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
  },
  '&.MuiTableCell-root': {
    padding: '8px 12px',
  },
  zIndex: 98,
}));

export const CellInputStyled = styled(Typography)({
  width: '100%',
  padding: '4px 20px',
  fontFamily: 'montserrat',
  background: 'none',
  display: 'flex',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: '12px',
});

export const LinearProgressStyled = styled(LinearProgress)({
  height: '8px',
  borderRadius: '20px',
});

export const TableCellStyled = styled(TableCell)(({ theme }) => ({
  border: '1px solid #e0e0e0',
  minWidth: '64px',
  textAlign: 'center',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
  },
  '&.MuiTableCell-root': {
    padding: '8px 12px',
  },
}));

export const TableCellHeaderFixedTotalStyled = styled(TableCell)(
  ({ theme }) => ({
    position: 'sticky',
    zIndex: 99,
    left: '120px',
    height: '48px',
    color: isColorDark(theme.palette.primary.main)
      ? theme.palette.global.white
      : theme.palette.global.black,
    fontWeight: 500,
    fontSize: '12px',
    backgroundColor: alpha(theme.palette.primary.main, 1),
    textAlign: 'center',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      color: isColorDark(theme.palette.secondary.main)
        ? theme.palette.global.white
        : theme.palette.global.black,
    },
    padding: '12px 6px',
    minWidth: '120px',
  })
);

export const TimelineNoneStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: alpha(theme.palette.global.lightgray, 0.5),
}));

export const IconStyled = styled(MdPendingActions)(({ theme }) => ({
  color: alpha(theme.palette.global.lightgray, 0.5),
  fontSize: 160,
}));
